import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faExternalLinkAlt, faChevronRight, faChevronDown, faFilePdf, faUpload, faCamera, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../context/AppContext';
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { findSkyCivPdfForDesign, openUploadInNewTab } from '../../utils/openUploadFile';
import { UpdateAddonQuantityForm } from '../forms/quotes/UpdateAddonQuantityForm';
import { ManageIncludedDesignsForm } from '../forms/quotes/ManageIncludedDesignsForm';
import { ADDON_ITEMS } from '../forms/quotes/UpdateAddonQuantityForm';
import { formatBillingAmount, formatBillingDate, formatInvoiceStatus } from '../../utils/billingTables';
import { ActionsDropdown, ActionsDropdownItem, ActionsDropdownDivider } from './ActionsDropdown';
import { TableEmptyState } from './TableEmptyState';

const getIncludedDesignIds = (row) => {
    const raw = row.included_designs ?? row.includedDesigns;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [];
};

const INVOICE_TABLE_COL_COUNT = 7;
const INVOICE_TABLE_COL_COUNT_WITHOUT_PROJECT = 6;

const ADDON_ACTION_LABELS = {
    'CHAIN-HOIST-BASIC': 'Update Chain Hoist',
    'LIFT-BRACKET-SQUARE': 'Update Lift Bracket',
};

export const InvoicesTable = ({ invoices, designs = [], files = [], onUploadTraveler, onUploadPhotosForDesign, hideProjectName = false, highlightUnpaid = false }) => {
    const colCount = hideProjectName ? INVOICE_TABLE_COL_COUNT_WITHOUT_PROJECT : INVOICE_TABLE_COL_COUNT;
    const { token, base_url, user } = useAppContext();
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [expandedInvoiceIds, setExpandedInvoiceIds] = useState({});
    const [addonModal, setAddonModal] = useState(null);
    const [manageDesignsModal, setManageDesignsModal] = useState(null);

    const setDropdownOpen = useCallback((invoiceId, open) => {
        setOpenDropdownId(open ? invoiceId : null);
    }, []);

    const toggleInvoiceDesignsExpanded = (invoiceId) => {
        setExpandedInvoiceIds((prev) => ({ ...prev, [invoiceId]: !prev[invoiceId] }));
    };
    
    const handleViewSkycivPdf = async (design) => {
        if (!design?.id) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        const upload = findSkyCivPdfForDesign(files, design.id);
        if (!upload) {
            window.showAlert(
                404,
                'No documents were found for this design. Please review the Files tab.'
            );
            return;
        }
        await openUploadInNewTab(upload, token, base_url);
    };

    const handleUploadTravelerClick = (design) => {
        if (!onUploadTraveler) {
            window.showAlert(
                400,
                'Open the project page to upload a traveler for this design.'
            );
            return;
        }
        if (!design?.id) {
            window.showAlert(
                404,
                'No design record is available for this row. Please review the Files tab.'
            );
            return;
        }
        onUploadTraveler(design);
    };

    const handleUploadPhotosClick = (design) => {
        if (!onUploadPhotosForDesign) {
            window.showAlert(
                400,
                'Open the project page to upload photos for this design.'
            );
            return;
        }
        if (!design?.id) {
            window.showAlert(
                404,
                'No design record is available for this row. Please review the Files tab.'
            );
            return;
        }
        onUploadPhotosForDesign(design);
    };

    // Open invoice payment page in a new tab
    const downloadPDF = async (invoice) => {
        if(!token) {
            window.showAlert(401, "Please login to download PDF");
        }
      
        const res = await fetch(`${base_url}/apis/billing/invoices/${invoice.id}/pdf`, {
          headers: {
            Authorization: token,
          },
        });
      
        if (!res.ok) {
          alert("Unauthorized or error fetching PDF");
          return;
        }
      
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
    };

    return (
        <div>
            <br />
            <div className="table-responsive">
                {invoices.length === 0 ? (
                    <TableEmptyState>
                        <p>No invoices available for this project.</p>
                        <p>Invoices will appear here once quotes are accepted and processed.</p>
                    </TableEmptyState>
                ) : (
                    <table className="data-table invoices-table billing-document-table">
                        <colgroup>
                            {hideProjectName ? null : <col className="col-lg hide-on-mobile" />}
                            <col className="col-quote-number" />
                            <col className="col-date hide-on-mobile" />
                            <col className="col-amount" />
                            <col className="col-amount hide-on-mobile" />
                            <col className="col-status hide-on-mobile" />
                            <col className="col-actions" />
                        </colgroup>
                        <thead>
                            <tr>
                                {!hideProjectName && <th className="col-lg hide-on-mobile">Project</th>}
                                <th className="col-quote-number">Invoice Number</th>
                                <th className="col-date hide-on-mobile">Due Date</th>
                                <th className="col-amount">Total</th>
                                <th className="col-amount hide-on-mobile">Balance</th>
                                <th className="col-status hide-on-mobile">Status</th>
                                <th className="col-actions billing-col-actions-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((invoice) => {
                                const includedIds = getIncludedDesignIds(invoice);
                                const designRows = includedIds.map((uid) => ({
                                    uid,
                                    design: designs.find((d) => d.unique_id === uid),
                                }));
                                const isDesignsExpanded = Boolean(expandedInvoiceIds[invoice.id]);

                                const isPaid = invoice.invoice_status?.toLowerCase() === 'paid';
                                const isUnpaid = !isPaid;
                                const isHighlighted = highlightUnpaid && user?.is_super_admin && isUnpaid;
                                const showAdminActions = Boolean(user?.is_super_admin);
                                const showActionsDropdown = isPaid || showAdminActions;

                                return (
                                    <React.Fragment key={invoice.id}>
                                        <tr className={`quote-main-row${isHighlighted ? ' table-row--highlighted' : ''}`}>
                                            {!hideProjectName && (
                                                <td className="col-lg hide-on-mobile">{invoice.project_site_name}</td>
                                            )}
                                            <td className="col-quote-number billing-doc-number-cell">
                                                <div className="billing-doc-number-row">
                                                    <span className="billing-doc-number-text">{invoice.invoice_number}</span>
                                                    {includedIds.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="quote-expand-btn"
                                                            onClick={() => toggleInvoiceDesignsExpanded(invoice.id)}
                                                            aria-expanded={isDesignsExpanded}
                                                            aria-label={isDesignsExpanded ? 'Hide designs on invoice' : 'Show designs on invoice'}
                                                            title={isDesignsExpanded ? 'Hide designs on invoice' : 'Show designs on invoice'}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={isDesignsExpanded ? faChevronDown : faChevronRight}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="col-date hide-on-mobile">{formatBillingDate(invoice.invoice_due_date)}</td>
                                            <td className="col-amount">{formatBillingAmount(invoice.total_amount)}</td>
                                            <td className="col-amount hide-on-mobile">{formatBillingAmount(invoice.remaining_balance)}</td>
                                            <td className="col-status hide-on-mobile">{formatInvoiceStatus(invoice.invoice_status)}</td>
                                            <td className="col-actions billing-col-actions-cell">
                                                <div className="invoice-actions-row">
                                                    {isUnpaid && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-action btn-primary invoice-view-pdf-btn"
                                                            title="View PDF"
                                                            onClick={() => downloadPDF(invoice)}
                                                        >
                                                            <FontAwesomeIcon icon={faFile} /> View PDF
                                                        </button>
                                                    )}
                                                    {showActionsDropdown && (
                                                        <ActionsDropdown
                                                            isOpen={openDropdownId === invoice.id}
                                                            onOpenChange={(open) => setDropdownOpen(invoice.id, open)}
                                                            estimatedMenuHeight={200}
                                                        >
                                                            {isPaid && (
                                                                <ActionsDropdownItem>
                                                                    <button
                                                                        className="btn btn-action btn-primary"
                                                                        title="View PDF"
                                                                        onClick={() => { downloadPDF(invoice); setOpenDropdownId(null); }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faFile} /> View PDF
                                                                    </button>
                                                                </ActionsDropdownItem>
                                                            )}
                                                            {showAdminActions && (
                                                                <>
                                                                    {isPaid && <ActionsDropdownDivider />}
                                                                    <ActionsDropdownItem>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-action btn-secondary"
                                                                            title="Manage included designs"
                                                                            onClick={() => {
                                                                                setManageDesignsModal({ type: 'invoice', record: invoice });
                                                                                setOpenDropdownId(null);
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faLayerGroup} /> Manage Designs
                                                                        </button>
                                                                    </ActionsDropdownItem>
                                                                    {invoice.qbo_data && (
                                                                        <ActionsDropdownItem>
                                                                            <a
                                                                                href={`https://qbo.intuit.com/app/invoice?txnId=${invoice.qbo_data['Id']}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-action btn-secondary"
                                                                                title="Open in QuickBooks"
                                                                            >
                                                                                <FontAwesomeIcon icon={faExternalLinkAlt} /> Open in QuickBooks
                                                                            </a>
                                                                        </ActionsDropdownItem>
                                                                    )}
                                                                    {ADDON_ITEMS.map((addon) => (
                                                                        <ActionsDropdownItem key={addon.sku}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-action btn-secondary"
                                                                                title={ADDON_ACTION_LABELS[addon.sku] ?? `Update ${addon.name}`}
                                                                                onClick={() => {
                                                                                    setAddonModal({ invoice, addon });
                                                                                    setOpenDropdownId(null);
                                                                                }}
                                                                            >
                                                                                {ADDON_ACTION_LABELS[addon.sku] ?? `Update ${addon.name}`}
                                                                            </button>
                                                                        </ActionsDropdownItem>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </ActionsDropdown>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {includedIds.length > 0 &&
                                            isDesignsExpanded &&
                                            designRows.map(({ uid, design }) => (
                                                <tr key={`${invoice.id}-design-${uid}`} className="quote-design-subrow">
                                                    <td colSpan={colCount}>
                                                        <div className="quote-design-subrow-inner">
                                                            <div className="quote-design-subrow-main">
                                                                {design ? (
                                                                    <>
                                                                        <Link
                                                                            className="quote-design-subrow-link"
                                                                            to={`/view-design/${design.id}`}
                                                                        >
                                                                            {design.design_name}
                                                                        </Link>
                                                                        <span className="quote-design-subrow-meta">
                                                                            <span className="quote-design-subrow-label">SKU</span>
                                                                            {design.unique_id}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <span className="quote-design-subrow-missing">
                                                                        <span className="quote-design-subrow-label">SKU</span>
                                                                        {uid}
                                                                        {designs.length > 0 && (
                                                                            <span className="quote-design-subrow-hint">
                                                                                {' '}
                                                                                (not found on this project)
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="quote-design-subrow-actions">
                                                                {user?.is_super_admin && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                        onClick={() => handleUploadPhotosClick(design)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faCamera} />
                                                                        Upload Photos
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                    onClick={() => handleViewSkycivPdf(design)}
                                                                >
                                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                                    View Skyciv PDF
                                                                </button>
                                                                {user?.is_super_admin && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-action btn-secondary design-subrow-action-btn"
                                                                        onClick={() => handleUploadTravelerClick(design)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faUpload} />
                                                                        Upload Traveler
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            {addonModal && (
                <UpdateAddonQuantityForm
                    invoice={addonModal.invoice}
                    addon={addonModal.addon}
                    isOpen={true}
                    onClose={() => setAddonModal(null)}
                />
            )}
            {manageDesignsModal && (
                <ManageIncludedDesignsForm
                    type={manageDesignsModal.type}
                    record={manageDesignsModal.record}
                    designs={designs}
                    isOpen={true}
                    onClose={() => setManageDesignsModal(null)}
                />
            )}

        </div>
    );
};
